import React, { useEffect, useState } from 'react';

import Table, { buildRows, currencyFormatter, TABLE_SORT_DIRECTION_ASC, TABLE_SORT_DIRECTION_DESC } from '../../components/Table/Table';
import useApi, { ApiArrayResponse } from '../../hooks/useApi';
import usePagination from '../../hooks/usePagination';
import useSorting from '../../hooks/useSorting';

export type BusinessesRow = {
  _id: string;
  business: string;
  name: string;
  amount_paid: number;
  amount_invested: number;
  total_products: number;
  active_offers: number;
  inactive_offers: number;
  likes: number;
  sepa: boolean;
};

const Businesses = () => {
  const { currentSorting, setCurrentSorting, getSortingParams } = useSorting('total_products', TABLE_SORT_DIRECTION_DESC);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const { status: callApiStatus, response: apiResponse, callApi } = useApi<ApiArrayResponse<BusinessesRow> & { businessUsers: { [business: string]: any } }>(
    'business-stats'
  );
  const { callApi: callChangeSepa } = useApi<ApiArrayResponse<BusinessesRow>>(
    'business-change-sepa', 'POST'
  );

  const { callApi: toggleUserEnabled } = useApi('user/update-enabled', 'POST');

  const pagination = usePagination();

  const header = [
    'Empresa',
    'Dinero ingresado',
    'Dinero invertido',
    'Total de productos en venta',
    'Ofertas en activo',
    'Ofertas en inactivo',
    'Likes recibidos',
    'SEPA',
    'Servicio activado'
  ];

  let rows: BusinessesRow[] = [];
  let users: { [business: string]: any } = {};
  let totalRows = 0;
  let shownRows = 0;

  if (apiResponse) {
    let { results, total, shownTotal, businessUsers } = apiResponse;
    totalRows = total;
    shownRows = shownTotal;
    rows = results;
    users = businessUsers;
  }

  useEffect(() => {
    callApi({ ...getSortingParams(), page: pagination.currentPage, search: searchTerm });
  }, [callApi, pagination.currentPage, getSortingParams, searchTerm]);

  const handleSearch = (searchTerm: string) => {
    pagination.setCurrentPage(0);
    setSearchTerm(searchTerm);
  };

  const changeSepa = async (id: string) => {
    await callChangeSepa(undefined, { id })
    callApi({ ...getSortingParams(), page: pagination.currentPage, search: searchTerm });
  }

  const toggleService = async (user: any) => {
    user.enabled = !user.enabled;
    await toggleUserEnabled(undefined, { id: user._id, enabled: user.enabled });

    callApi({ ...getSortingParams(), page: pagination.currentPage, search: searchTerm });
  }

  return (
    <React.Fragment>
      <div id="app-content">
        <div id="header" className="d-flex justify-content-between">
          <h1>Empresas</h1>
          <span>Total de empresas: {totalRows}</span>
        </div>
        <Table
          header={header}
          rows={buildRows(
            rows.map(row => ({
              ...row,
              amount_paid: currencyFormatter(row['amount_paid']),
              amount_invested: currencyFormatter(row['amount_invested']),
              sepa: (
                <span
                  className="btn-action"
                  onClick={() => { changeSepa(row._id) }}
                >
                  {row['sepa'] ? 'Si' : 'No'}
                </span>
              ),
              enabled: (
                <span
                  className="btn-action"
                  onClick={() => { toggleService(users[row.business]) }}
                >
                  {users[row.business].enabled ? 'Si' : 'No'}
                </span>
              )
            })),
            ['name', 'amount_paid', 'amount_invested', 'total_products', 'active_offers', 'inactive_offers', 'likes', 'sepa', 'enabled']
          )}
          sorting={[
            {
              key: 'total_products_desc',
              column: 'total_products',
              label: 'Empresas que más venden',
              direction: TABLE_SORT_DIRECTION_DESC,
              selected: currentSorting.column === 'total_products' &&
                currentSorting.direction === TABLE_SORT_DIRECTION_DESC
            },
            {
              key: 'total_products_asc',
              column: 'total_products',
              label: 'Empresas que menos venden',
              direction: TABLE_SORT_DIRECTION_ASC,
              selected: currentSorting.column === 'total_products' &&
                currentSorting.direction === TABLE_SORT_DIRECTION_ASC
            }
          ]}
          onSort={selectedSorting => setCurrentSorting(selectedSorting)}
          onSearch={handleSearch}
          loading={callApiStatus === 'pending'}
          pagination={pagination}
          totalEntries={shownRows}
        />
      </div>
    </React.Fragment>
  );
};

export default Businesses;
